import { useCallback, useEffect, useRef, useState } from 'react'

import { easeInOutQuadWithGap } from '~/shared/helpers/animationFunctions'

const frameDuration = 1000 / 60

export const useProgressAnimation = (animation: {
  duration: number
  from?: number
  to?: number
  animationFunction?: (t: number) => number
  callback?: () => void
}): {
  counter: number
  progressPercent: number
  isFinished: boolean
  progressAnimationPlayback: (stopAt?: number) => void
} => {
  const {
    to: countTo = 100,
    duration: animationDuration,
    animationFunction = easeInOutQuadWithGap,
    callback,
  } = animation

  const [counterValue, setCounterValue] = useState(0)
  const [progressPercent, setProgressPercent] = useState(0)
  const frame = useRef(1)
  const callbackRef = useRef<typeof callback>(callback)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const progressAnimationPlayback = useCallback(
    (stopAt?: number) => {
      const totalFrames = Math.round(animationDuration / frameDuration)

      if (intervalRef.current) clearInterval(intervalRef.current)

      intervalRef.current = setInterval(() => {
        const result = countTo * animationFunction(frame.current / totalFrames)

        if (!result) return
        if (stopAt && Math.round(result) === stopAt + 1) {
          if (intervalRef.current) clearInterval(intervalRef.current)
          intervalRef.current = null

          return
        }

        frame.current += 1
        setCounterValue(Math.round(result))
        setProgressPercent(Math.min((result / countTo) * 100, 100))

        const isAnimationInProgress = frame?.current !== totalFrames
        if (isAnimationInProgress) return

        if (intervalRef.current) clearInterval(intervalRef.current)
        intervalRef.current = null
      }, frameDuration)
    },
    [animationDuration, countTo, animationFunction],
  )

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>

    if (counterValue >= countTo && callbackRef.current) {
      timerId = setTimeout(() => callbackRef.current?.(), 0)
    }
    return () => clearTimeout(timerId)
  }, [counterValue, countTo])

  useEffect(() => () => setCounterValue(0), [])

  // Guards against a running interval outliving the component: without this,
  // navigating away mid-animation leaves the 60fps tick firing against an
  // unmounted component until it happens to reach its own end condition.
  useEffect(
    () => () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    },
    [],
  )

  return {
    counter: counterValue,
    progressPercent: Math.round(progressPercent),
    isFinished: counterValue === countTo,
    progressAnimationPlayback,
  }
}
